import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Sprite, Container } from "@pixi/react";
import { useButtonHover } from "../../../../Hooks/useButtonHover";
import { useTranslation } from "react-i18next";
import { SOUND_NAME } from "../../../../sounds";
import { getUITexture } from "../../../../assets";
import { PixiSettings } from "../../../../pixiSettings";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PRIMARY_USER_INTERFACE_BUTTON_CONFIG } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { useReelControl } from "../../../../Contexts/ReelControlContext";
import { selectIsSpinning } from "../../../../State/selectors";

// Position config — extracted for easy tweaking
const HOME_BUTTON_POSITION_CONFIG = {
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar, buttonConfig) => buttonConfig.width * 0.9,
      y: (toolbar) => toolbar.height / 2,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => buttonConfig.width * 0.9,
      y: (toolbar) => toolbar.height / 2,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar, buttonConfig) => buttonConfig.width * 0.9,
      y: (toolbar) => toolbar.height / 2,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => buttonConfig.width * 0.9,
      y: (toolbar) => toolbar.height / 2,
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: {
    x: (toolbar, buttonConfig) => buttonConfig.width,
    y: (toolbar) => toolbar.height / 2,
  },
};

const ButtonHome = ({ toolbar }) => {
  const { t } = useTranslation();
  const { getResponsiveConfig, device, orientation } = useDimensions();
  const { handleSpin } = useReelControl();
  const isSpinning = useSelector(selectIsSpinning);

  const {
    hoverContainerRef,
    buttonRef,
    handlePointerOver,
    handlePointerOut,
    triggerPress,
  } = useButtonHover();

  const texture = getUITexture("icon_home");

  const buttonConfig = useMemo(
    () => getResponsiveConfig(PRIMARY_USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // Position using config
  const buttonPosition = useMemo(() => {
    const deviceConfig = HOME_BUTTON_POSITION_CONFIG[device];
    const posConfig = deviceConfig?.[orientation] ?? deviceConfig;
    return {
      x: posConfig.x(toolbar, buttonConfig),
      y: posConfig.y(toolbar, buttonConfig),
    };
  }, [device, orientation, toolbar, buttonConfig]);

  // TEMP: Trigger bonus buy spin for Royal Collect testing
  const handleClick = () => {
    if (isSpinning) return;
    soundManager.play(SOUND_NAME.button_click);
    triggerPress();
    handleSpin({ purchasedFeature: "bonus_buy" });
  };

  return (
    <Container
      name="secondary_button_home"
      x={buttonPosition.x}
      y={buttonPosition.y}
      zIndex={2}
    >
      <Container ref={hoverContainerRef}>
        <Sprite
          ref={buttonRef}
          texture={texture}
          name="secondary_button_home"
          anchor={0.5}
          width={buttonConfig.width}
          height={buttonConfig.height}
          x={0}
          y={0}
          eventMode="static"
          cursor="pointer"
          onpointertap={handleClick}
          onpointerover={handlePointerOver}
          onpointerout={handlePointerOut}
          accessible={true}
          accessibleTitle={t("home", { ns: "common" })}
          accessibleHint={t("home", { ns: "common" })}
          resolution={2}
        />
      </Container>
    </Container>
  );
};

export default ButtonHome;
